/**
 * Strategic Performance Module - Settings Hooks
 * 
 * Custom hooks for Departments, Employees, and Organization settings
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  departmentsAPI,
  employeesAPI,
  organizationAPI,
} from '../services/strategicPerformanceApi';
import {
  SPDepartment,
  SPEmployee,
  OrganizationSettings,
  CreateDepartmentDTO,
  UpdateDepartmentDTO,
  CreateEmployeeDTO,
  UpdateEmployeeDTO,
  EmployeeRole,
} from '../types';

// =============================================================================
// DEPARTMENTS HOOKS
// =============================================================================

export function useDepartments() {
  const [departments, setDepartments] = useState<SPDepartment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const loadDepartments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await departmentsAPI.getAll();
      setDepartments(data);
    } catch (err: any) {
      setError(err);
      console.error('Error loading departments:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  return { data: departments, isLoading, error, refetch: loadDepartments };
}

export function useDepartment(id: string | undefined) {
  const [department, setDepartment] = useState<SPDepartment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!id) {
      setDepartment(null);
      setIsLoading(false);
      return;
    }

    const loadDepartment = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await departmentsAPI.getById(id);
        setDepartment(data);
      } catch (err: any) {
        setError(err);
        console.error('Error loading department:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadDepartment();
  }, [id]);

  return { data: department, isLoading, error };
}

export function useCreateDepartment() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async (data: CreateDepartmentDTO) => {
    try {
      setIsPending(true);
      const newDepartment = await departmentsAPI.create(data);
      toast.success(`Department "${newDepartment.name}" created successfully`);
      return newDepartment;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create department');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

export function useUpdateDepartment() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async ({ id, data }: { id: string; data: UpdateDepartmentDTO }) => {
    try {
      setIsPending(true);
      const updatedDepartment = await departmentsAPI.update(id, data);
      toast.success(`Department "${updatedDepartment.name}" updated successfully`);
      return updatedDepartment;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update department');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

export function useDeleteDepartment() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async (id: string) => {
    try {
      setIsPending(true);
      await departmentsAPI.delete(id);
      toast.success('Department deleted successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete department. It may have employees assigned.');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

// =============================================================================
// EMPLOYEES HOOKS
// =============================================================================

export function useEmployees(filters?: { departmentId?: string; role?: EmployeeRole; status?: 'active' | 'inactive' }) {
  const [employees, setEmployees] = useState<SPEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const loadEmployees = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await employeesAPI.getAll(filters);
      setEmployees(data);
    } catch (err: any) {
      setError(err);
      console.error('Error loading employees:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters?.departmentId, filters?.role, filters?.status]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  return { data: employees, isLoading, error, refetch: loadEmployees };
}

export function useEmployee(id: string | undefined) {
  const [employee, setEmployee] = useState<SPEmployee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!id) {
      setEmployee(null);
      setIsLoading(false);
      return;
    }

    const loadEmployee = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await employeesAPI.getById(id);
        setEmployee(data);
      } catch (err: any) {
        setError(err);
        console.error('Error loading employee:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployee();
  }, [id]);

  return { data: employee, isLoading, error };
}

export function useEmployeesByDepartment(departmentId: string | undefined) {
  return useEmployees({ departmentId });
}


export function useCreateEmployee() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async (data: CreateEmployeeDTO) => {
    try {
      setIsPending(true);
      const newEmployee = await employeesAPI.create(data);
      toast.success(`Employee "${newEmployee.fullName}" created successfully`);
      return newEmployee;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create employee');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

export function useUpdateEmployee() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async ({ id, data }: { id: string; data: UpdateEmployeeDTO }) => {
    try {
      setIsPending(true);
      const updatedEmployee = await employeesAPI.update(id, data);
      toast.success(`Employee "${updatedEmployee.fullName}" updated successfully`);
      return updatedEmployee;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update employee');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

export function useDeleteEmployee() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async (id: string) => {
    try {
      setIsPending(true);
      await employeesAPI.delete(id);
      toast.success('Employee deleted successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete employee');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

// =============================================================================
// ORGANIZATION SETTINGS HOOKS
// =============================================================================

export function useOrganizationSettings() {
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await organizationAPI.getSettings();
      setSettings(data);
    } catch (err: any) {
      setError(err);
      console.error('Error loading organization settings:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return { data: settings, isLoading, error, refetch: loadSettings };
}

export function useUpdateOrganizationSettings() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async (data: Partial<OrganizationSettings>) => {
    try {
      setIsPending(true);
      await organizationAPI.updateSettings(data);
      toast.success('Organization settings updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update organization settings');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

export function useUploadLogo() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async (file: File) => {
    try {
      setIsPending(true);
      await organizationAPI.uploadLogo(file);
      toast.success('Logo uploaded successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to upload logo');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}

export function useDeleteLogo() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback(async () => {
    try {
      setIsPending(true);
      await organizationAPI.deleteLogo();
      toast.success('Logo removed successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove logo');
      throw error;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync: mutate, isPending };
}
